import { Directionality } from '@angular/cdk/bidi';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { LucideArrowDown, LucideChevronsUpDown } from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';
import { DEFAULT_PAGE_SIZE_OPTIONS, PageSizeOption, PaginationEvent } from '../pagination/pagination-footer.component';
import { PtlTableCellDirective } from './ptl-table-cell.directive';
import { PtlTableHeaderDirective } from './ptl-table-header.directive';

export interface PtlTableColumn {
  key: string;
  label: string;
  /** Accessible name for columns with an empty visible `label` (e.g. an actions/menu column). */
  ariaLabel?: string;
  /**
   * Which `<ng-template ptlCell="…">` renders this column, when that is not `key`.
   * Columns generated from data cannot each have a template authored for them —
   * they all point at one shared template instead and read the row by `key`.
   */
  templateKey?: string;
  /**
   * Which `<ng-template ptlHeaderCell="…">` renders this column's header, when
   * that is not `key`. Without a matching template the header is `label`.
   */
  headerTemplateKey?: string;
  /** Logical alignment, so RTL locales mirror correctly. Defaults to `end` for numeric columns, `start` otherwise. */
  align?: 'start' | 'center' | 'end';
  /**
   * Counts and amounts the reader compares down the column: aligned to the end so digits line up
   * by place value, and rendered with tabular figures so they do not shift width. Not for numbers
   * that are really labels (year, room number) — those stay text.
   */
  numeric?: boolean;
  width?: string;
  sortable?: boolean;
  /**
   * Clamps the cell to `width` and ellipsises the overflow. Without it a long
   * value stretches the column past its declared width — `width` is only a hint
   * under the automatic table layout — and pushes the table into a sideways scroll.
   */
  truncate?: boolean;
  /**
   * Set to false for columns whose data source sorts in one fixed direction
   * (several admin endpoints take a sort key but no direction). Such columns
   * toggle on/off instead of cycling asc/desc, and show a neutral indicator.
   */
  sortDirectional?: boolean;
}

export interface PtlTableSort {
  key: string;
  direction: 'asc' | 'desc';
}

/** Weight given to a column that declares no width, so it still gets a fair share. */
const DEFAULT_COLUMN_WEIGHT = 200;
const SELECT_COLUMN_WEIGHT = 28;

const SKELETON_BAR_WIDTHS = ['70%', '45%', '85%', '55%', '60%', '40%'];
const SKELETON_MAX_ROWS = 8;
const SKELETON_FALLBACK_ROWS = 6;

/**
 * Shared v3 data table. Owns the card chrome, header (with optional client-side
 * sort), body, and a pinned pagination footer. The consumer projects a
 * `[table-toolbar]` element (search / filters / actions) into the top bar, and
 * `<ng-template ptlCell="key" let-row>` templates for custom cell content.
 * Search / filtering stay in the parent: it feeds already-filtered `data`.
 */
@Component({
  selector: 'ptl-data-table',
  templateUrl: './ptl-table.component.html',
  styleUrls: ['./ptl-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PtlTableComponent implements AfterContentInit, OnChanges {
  @Input() columns: PtlTableColumn[] = [];
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  @Input() data: Record<string, any>[] = [];
  /** 0 = no pagination (footer hidden). Updated by the footer's "rows per page" select. */
  @Input() pageSize = 0;
  /** Footer label; "{count}" is replaced with the total row count. */
  @Input() totalLabel = '';
  /** Row property used for trackBy (falls back to index). */
  @Input() trackKey = '';
  /** Renders the header row with a dark background and light text. */
  @Input() darkHeader = false;
  /**
   * Caps the row area at roughly one viewport and scrolls it on its own, so the
   * column header stays pinned at the top and the pagination footer stays in
   * reach instead of sitting a full page-scroll away. Only engages once the rows
   * outgrow the cap. Override the cap per view with `--ptl-table-max-height`.
   */
  @Input() fitToViewport = true;
  /**
   * Server-side mode: `data` is a single already-fetched page, rendered as-is
   * (no client-side sort/slice). `totalItems` drives the footer, and the parent
   * reacts to (pageChange)/(pageSizeChange)/(sortChange) by re-fetching.
   */
  @Input() serverSide = false;
  /** Total row count across all pages. Only used when `serverSide` is true. */
  @Input() totalItems = 0;
  /** Current zero-based page: drives the footer in server-side mode, seeds the opening page in client-side mode. */
  @Input() currentPage = 0;
  /** Adds a leading checkbox column. Requires `trackKey` to identify rows. */
  @Input() selectable = false;
  /** Keys (`trackKey` values) of the selected rows. */
  @Input() selectedKeys: unknown[] = [];
  /** Aria label for the select-all checkbox. */
  @Input() selectAllAriaLabel = '';
  /** Builds the aria label for a row's checkbox, e.g. `(row) => 'Select ' + row.title`. */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  @Input() rowSelectAriaLabel?: (row: Record<string, any>) => string;
  /** Aria label describing the table as a whole. */
  @Input() tableAriaLabel = '';
  /** Builds the aria label for a row, e.g. `(row) => 'Open ' + row.title`. */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  @Input() rowAriaLabel?: (row: Record<string, any>) => string;
  /** Page-size choices for the footer. Defaults to the footer's own options. */
  @Input() pageSizeOptions?: PageSizeOption[];
  /**
   * Swaps the body for skeleton rows and freezes the header and footer controls.
   * Keeps the card, columns and pagination in place, so nothing shifts when the
   * real rows arrive — and no empty state flashes before the first response.
   */
  @Input() loading = false;
  /** Number of skeleton rows. Defaults to the page size, capped at 8. */
  @Input() skeletonRows?: number;

  /**
   * `fit` (the default) normalises the declared widths into proportions of the
   * container, so the table never scrolls sideways. `scroll` honours them
   * literally and lets the row area scroll horizontally — for a matrix whose
   * column count comes from the data and cannot be squeezed into one screen
   * without the cells becoming unreadable.
   */
  @Input() layout: 'fit' | 'scroll' = 'fit';

  /**
   * How many leading columns stay pinned while the rest scroll under them. Only
   * meaningful with `layout="scroll"`: without an identity column in view, a row
   * of grades halfway across a matrix belongs to nobody. Requires those columns
   * to declare a `width`, since the pinned offset is computed from it.
   */
  @Input() stickyColumns = 0;

  /**
   * Seeds the visible sort indicator — needed when the sort comes from outside
   * the table (restored filters, a toolbar sort select, a server default).
   */
  @Input() set sort(value: PtlTableSort | null) {
    this.sortKey = value?.key ?? null;
    this.sortDir = value?.direction ?? 'asc';
  }

  @Output() sortChange = new EventEmitter<PtlTableSort | null>();
  @Output() pageChange = new EventEmitter<PaginationEvent>();
  /** In server-side mode the handler must refetch from the first page — the old offset is meaningless at a new size. */
  @Output() pageSizeChange = new EventEmitter<number>();
  /** Emits the full set of selected keys whenever the selection changes. */
  @Output() selectedKeysChange = new EventEmitter<unknown[]>();
  /** Emits the clicked body row. Opt-in: rows are only interactive when a consumer binds this. */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  @Output() rowClick = new EventEmitter<Record<string, any>>();

  @ContentChildren(PtlTableCellDirective) private cellDirs!: QueryList<PtlTableCellDirective>;
  @ContentChildren(PtlTableHeaderDirective) private headerDirs!: QueryList<PtlTableHeaderDirective>;
  @ViewChild('scrollArea') private scrollArea?: ElementRef<HTMLElement>;

  private hostRef = inject(ElementRef<HTMLElement>);
  private directionality = inject(Directionality);
  private transloco = inject(TranslocoService);

  /** Effective select-all label — the consumer's input, else a translated default. */
  protected get effectiveSelectAllAriaLabel(): string {
    return this.selectAllAriaLabel || this.transloco.translate('translations.global.ariaLabels.selectAllRows');
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  protected resolveRowSelectAriaLabel(row: Record<string, any>): string {
    return this.rowSelectAriaLabel?.(row) || this.transloco.translate('translations.global.ariaLabels.selectRow');
  }

  protected readonly LucideArrowDown = LucideArrowDown;
  protected readonly LucideChevronsUpDown = LucideChevronsUpDown;

  get footerPageSizeOptions(): PageSizeOption[] {
    return this.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;
  }

  sortKey: string | null = null;
  sortDir: 'asc' | 'desc' = 'asc';
  page = 0;

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  private cellMap = new Map<string, TemplateRef<any>>();
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  private headerMap = new Map<string, TemplateRef<any>>();
  private selectedSet = new Set<unknown>();
  private skeletonRowCache: number[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.serverSide && changes['currentPage']) {
      this.page = Math.max(0, this.currentPage);
    }
    if (!this.serverSide && this.pageSize && this.rows.length && this.page * this.pageSize >= this.rows.length) {
      this.page = 0;
    }
    this.selectedSet = new Set(this.selectedKeys);
  }

  ngAfterContentInit(): void {
    this.buildCellMap();
    this.buildHeaderMap();
    this.cellDirs.changes.subscribe(() => this.buildCellMap());
    this.headerDirs.changes.subscribe(() => this.buildHeaderMap());
  }

  private buildCellMap(): void {
    this.cellMap.clear();
    this.cellDirs.forEach((d) => this.cellMap.set(d.column, d.template));
  }

  private buildHeaderMap(): void {
    this.headerMap.clear();
    this.headerDirs.forEach((d) => this.headerMap.set(d.column, d.template));
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  cellTemplate(col: PtlTableColumn): TemplateRef<any> | null {
    return this.cellMap.get(col.templateKey ?? col.key) ?? null;
  }

  /** Null for the ordinary case, where the header is just `label`. */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  headerTemplate(col: PtlTableColumn): TemplateRef<any> | null {
    return this.headerMap.get(col.headerTemplateKey ?? col.key) ?? null;
  }

  /**
   * Declared widths become proportions of the table. Under `table-layout: fixed`
   * a literal width would be honoured to the pixel, so a set of widths summing
   * past the container would force a sideways scroll; as proportions they always
   * fit, keep the authored ratios, and stay identical from page to page.
   *
   * `layout="scroll"` wants exactly that sideways scroll, so there the declared
   * width is passed through untouched.
   */
  columnWidth(col: PtlTableColumn): string | null {
    if (this.layout === 'scroll') {
      return col.width ?? null;
    }
    const total = this.widthTotal;
    return total ? `${((this.columnWeight(col) / total) * 100).toFixed(4)}%` : null;
  }

  get selectColumnWidth(): string | null {
    if (this.layout === 'scroll') {
      return `${SELECT_COLUMN_WEIGHT / 16}rem`;
    }
    const total = this.widthTotal;
    return total ? `${((SELECT_COLUMN_WEIGHT / total) * 100).toFixed(4)}%` : null;
  }

  /** True for the leading columns pinned against the start edge while the rest scroll. */
  isSticky(index: number): boolean {
    return this.layout === 'scroll' && index < this.stickyColumns;
  }

  /** Where there are pinned columns the select column pins too, ahead of them all. */
  get selectSticky(): boolean {
    return this.layout === 'scroll' && this.stickyColumns > 0;
  }

  /**
   * Distance from the start edge for a pinned column — the widths of the pinned
   * columns before it, plus the select column when there is one. Returned as a
   * logical `inset-inline-start` so RTL locales pin against the right edge.
   */
  stickyOffset(index: number): string | null {
    if (!this.isSticky(index)) {
      return null;
    }
    const preceding = this.columns
      .slice(0, index)
      .reduce((sum, col) => sum + this.columnWeight(col), this.selectable ? SELECT_COLUMN_WEIGHT : 0);
    return `${preceding / 16}rem`;
  }

  private get widthTotal(): number {
    const columns = this.columns.reduce((sum, col) => sum + this.columnWeight(col), 0);
    return columns + (this.selectable ? SELECT_COLUMN_WEIGHT : 0);
  }

  private columnWeight(col: PtlTableColumn): number {
    const match = /^([\d.]+)\s*(rem|px)$/.exec(col.width?.trim() ?? '');
    if (!match) {
      return DEFAULT_COLUMN_WEIGHT;
    }
    const value = parseFloat(match[1]);
    return match[2] === 'rem' ? value * 16 : value;
  }

  /**
   * Resolved to a physical side on purpose. Cells carry dir="auto" so a Latin title in an RTL
   * table truncates at its own end; that would also flip how `start`/`end` resolve, splitting one
   * column across both edges. Deciding the side here keeps every row on the column's edge.
   */
  cellAlign(col: PtlTableColumn): string {
    const logical = col.align ?? (col.numeric ? 'end' : 'start');
    if (logical === 'center') {
      return 'center';
    }
    const startSide = this.directionality.value === 'rtl' ? 'right' : 'left';
    return logical === 'start' ? startSide : startSide === 'left' ? 'right' : 'left';
  }

  /**
   * dir="auto" resolves from the cell's text, which is right for plain values but wrong for
   * templated cells — a Latin value would flip the template's whole flex layout to LTR inside
   * an RTL table. Templated cells keep the page direction.
   */
  cellDir(col: PtlTableColumn): string | null {
    return this.cellTemplate(col) ? null : 'auto';
  }

  /** Hover text for truncated plain cells. Templated cells carry their own tooltip. */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  truncateTitle(row: Record<string, any>, col: PtlTableColumn): string | null {
    if (this.cellTemplate(col)) {
      return null;
    }
    const value = row[col.key];
    return value == null || value === '' ? null : String(value);
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  private get rows(): Record<string, any>[] {
    return this.data ?? [];
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  get sortedRows(): Record<string, any>[] {
    const key = this.sortKey;
    if (!key || this.serverSide) {
      return this.rows;
    }
    const dir = this.sortDir === 'asc' ? 1 : -1;
    return [...this.rows].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
    });
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  get pagedRows(): Record<string, any>[] {
    const rows = this.sortedRows;
    if (!this.pageSize || this.serverSide) {
      return rows;
    }
    const start = this.page * this.pageSize;
    return rows.slice(start, start + this.pageSize);
  }

  get total(): number {
    return this.serverSide ? this.totalItems : this.rows.length;
  }

  get displayPage(): number {
    return this.serverSide ? this.currentPage : this.page;
  }

  /** Rows become interactive (cursor, keyboard, click) only when a consumer subscribes to (rowClick). */
  get rowClickable(): boolean {
    return this.rowClick.observed;
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  onRowClick(row: Record<string, any>): void {
    if (this.rowClickable) {
      this.rowClick.emit(row);
    }
  }

  get totalText(): string {
    return this.totalLabel.replace('{count}', String(this.total));
  }

  /** The card keeps its table chrome while loading, so the empty state never flashes mid-fetch. */
  get showsTable(): boolean {
    return this.loading || this.total > 0;
  }

  get skeletonRowList(): number[] {
    // Match the outgoing page row for row, so paging does not resize the card.
    const fallback = Math.min(this.pageSize || SKELETON_FALLBACK_ROWS, SKELETON_MAX_ROWS);
    const count = Math.max(1, this.skeletonRows ?? (this.rows.length || fallback));
    if (this.skeletonRowCache.length !== count) {
      this.skeletonRowCache = Array.from({ length: count }, (_, index) => index);
    }
    return this.skeletonRowCache;
  }

  /** Deterministic — random widths would shift on every change-detection pass. */
  skeletonBarWidth(rowIndex: number, colIndex: number): string {
    return SKELETON_BAR_WIDTHS[(rowIndex * 3 + colIndex) % SKELETON_BAR_WIDTHS.length];
  }

  onSort(col: PtlTableColumn): void {
    if (!col.sortable || this.loading) {
      return;
    }
    const directional = col.sortDirectional !== false;
    if (this.sortKey !== col.key) {
      this.sortKey = col.key;
      this.sortDir = 'asc';
    } else if (directional && this.sortDir === 'asc') {
      this.sortDir = 'desc';
    } else {
      this.sortKey = null;
      this.sortDir = 'asc';
    }
    this.page = 0;
    this.sortChange.emit(this.sortKey ? { key: this.sortKey, direction: this.sortDir } : null);
  }

  onPage(event: PaginationEvent): void {
    if (!this.serverSide) {
      this.page = Math.max(0, event.currentPage - 1);
    }
    this.pageChange.emit(event);
    this.scrollToTop();
  }

  /** Brings the first row back into view — a new page always starts at the top. */
  private scrollToTop(): void {
    const scrollArea = this.scrollArea?.nativeElement;
    if (scrollArea) {
      scrollArea.scrollLeft = 0;
    }
    const host = this.hostRef.nativeElement;
    if (host.getBoundingClientRect().top < 0) {
      host.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  onPageSize(size: number): void {
    if (!this.serverSide) {
      this.pageSize = size;
      this.page = 0;
    }
    this.pageSizeChange.emit(size);
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  trackRow = (index: number, row: Record<string, any>): unknown => (this.trackKey ? (row[this.trackKey] ?? index) : index);

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  isRowSelected(row: Record<string, any>): boolean {
    return this.selectedSet.has(row[this.trackKey]);
  }

  get allRowsSelected(): boolean {
    const rows = this.pagedRows;
    return rows.length > 0 && rows.every((row) => this.selectedSet.has(row[this.trackKey]));
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  onRowSelectionToggle(row: Record<string, any>, selected: boolean, event?: Event): void {
    event?.stopPropagation();
    const next = new Set(this.selectedSet);
    if (selected) {
      next.add(row[this.trackKey]);
    } else {
      next.delete(row[this.trackKey]);
    }
    this.emitSelection(next);
  }

  onSelectAllToggle(selected: boolean): void {
    const next = new Set(this.selectedSet);
    for (const row of this.pagedRows) {
      if (selected) {
        next.add(row[this.trackKey]);
      } else {
        next.delete(row[this.trackKey]);
      }
    }
    this.emitSelection(next);
  }

  private emitSelection(next: Set<unknown>): void {
    this.selectedSet = next;
    this.selectedKeys = Array.from(next);
    this.selectedKeysChange.emit(this.selectedKeys);
  }
}
